/** Admin Matching Center: review requirements, choose which candidates a company may see, opportunities, introductions. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { ago, fmtDate } from '../../core/format.js';
import { navigate } from '../../core/router.js';
import { currentView, reload, update } from '../../app/shell.js';
import { backLink, banner, chips, empty, ic, pageHead, seg, statusTag, table, tag } from '../../ui/components.js';
import { field, selectField, tagField } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { filterSelect, queryOf, withQuery } from './shared.js';

const REQ_STATUS = [['in_review', 'In review'], ['open', 'Open'], ['matched', 'Matched'], ['closed', 'Closed'], ['rejected', 'Rejected']];

/* ---------- requirements ---------- */

const requirements = {
  path: '/admin/requirements',
  roles: ['admin'],
  title: 'Requirements',
  async load(ctx) {
    const query = queryOf(ctx);
    const { requirements: list } = await api.get('/admin/requirements', { signal: ctx.signal, query: query.status ? { status: query.status } : {} });
    return { list, query };
  },
  view({ list, query }) {
    return html`${pageHead('Requirements', 'Review what companies ask for, then choose the candidates they may see.')}
      <div class="row mb">${filterSelect('Status', 'status', REQ_STATUS, query.status)}</div>
      ${list.length
        ? table(['Requirement', 'From', 'Type', 'Skills', 'Matched', 'Status', ''], list.map((r) => [html`<b>${r.title}</b><div class="xs mu">${r.ref}, ${fmtDate(r.created_at)}</div>`,
          html`${r.org_name}<div class="xs mu">${r.contact_name}${r.contact_email ? `, ${r.contact_email}` : ''}${r.contact_phone ? `, ${r.contact_phone}` : ''}</div>`, r.type, chips(r.skills), r.matched, statusTag(r.status),
          r.status === 'in_review'
            ? html`<div class="row nw"><button type="button" class="btn sm" data-act="req.accept" data-id="${r.id}" data-title="${r.title}">Accept</button><button type="button" class="btn danger sm" data-act="req.reject" data-id="${r.id}" data-title="${r.title}">Reject</button></div>`
            : html`<a class="btn ghost sm" href="/admin/requirements/${r.id}">${r.status === 'closed' ? 'View' : 'Candidates'}</a>`]))
        : empty('target', 'Nothing here', 'No requirements match this filter.')}`;
  },
};

register({
  'req.accept': async (el) => {
    if (!(await confirmBox({ title: `Accept ${el.dataset.title}?`, text: 'It opens for matching and for freelancer applications.', label: 'Accept' }))) return;
    await api.post(`/admin/requirements/${el.dataset.id}/review`, { body: {} });
    toast('Requirement accepted', 'check');
    await navigate(`/admin/requirements/${el.dataset.id}`);
  },
  'req.reject': (el) => openModal({
    title: `Reject ${el.dataset.title}`,
    body: html`<form id="rej-form" data-submit="req.reject.send" data-id="${el.dataset.id}" novalidate>${field('rj-reason', 'Reason (the company is told)', { name: 'reason', rows: 3, minlen: 3, max: 300 })}</form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="rej-form" class="btn danger">Reject</button>`,
  }),
  'req.reject.send': (form) => submitForm(form, async ({ reason }) => {
    await api.post(`/admin/requirements/${form.dataset.id}/review`, { body: { reject_reason: reason } });
    closeModal(false);
    toast('Requirement rejected', 'out');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

const requirementDetail = {
  path: '/admin/requirements/:id',
  roles: ['admin'],
  nav: 'requirements',
  title: (d) => (d ? d.requirement.title : 'Requirement'),
  ui: () => ({ picked: new Set() }),
  load: (ctx) => api.get(`/admin/requirements/${Number(ctx.params.id)}`, { signal: ctx.signal, query: ctx.query.get('all') === '1' ? { all: 1 } : {} }),
  view(d, ui, ctx) {
    const r = d.requirement;
    const all = ctx.query.get('all') === '1';
    const open = d.candidates.filter((c) => !c.approved);
    return html`${backLink('/admin/requirements', 'Requirements')}
      <div class="page-h"><div><h2 style="margin:0 0 4px">${r.title}</h2><p>${r.org_name}. ${r.count} needed. ${r.contact_name} ${r.contact_email}</p></div>${statusTag(r.status)}</div>
      ${chips(r.skills)}
      ${r.status === 'in_review' ? banner('Accept this requirement before sending candidates.', 'wn', 'info') : ''}
      <div class="row between mt mb"><h3 style="margin:0">Ranked candidates</h3><a class="btn ghost sm" href="${withQuery(`/admin/requirements/${r.id}`, { all: all ? '' : '1' })}">${all ? 'Show best matches only' : 'Show everyone above the minimum score'}</a></div>
      ${d.candidates.length ? html`${table(['', 'Candidate', 'Match', 'Assessments', 'Skills', ''], d.candidates.map((c) => [
        c.approved ? '' : html`<input type="checkbox" aria-label="Select ${c.name}" data-change="match.pick" data-id="${c.student_id}" ${ui.picked.has(c.student_id) ? 'checked' : ''}>`,
        html`<b>${c.name}</b>`, `${c.score}%`, c.assessment_avg != null ? `${c.assessment_avg}%` : html`<span class="mu">None</span>`, chips(c.skills), c.approved ? tag('Sent to company', 'ok') : '']))}
        <div class="row mt-s"><button type="button" class="btn" data-act="match.approve" data-id="${r.id}" ${ui.picked.size && r.status !== 'in_review' ? '' : 'disabled'}>${ic('send')} Send ${ui.picked.size || ''} selected to company</button>
          <span class="sm mu">${open.length} not yet sent. The company sees only the people you send.</span></div>`
        : empty('target', 'No candidates yet', 'No students meet the minimum score. Try showing everyone, or lower the minimum in Settings.')}`;
  },
};

register({
  'match.pick': (el) => update((ui) => { if (el.checked) ui.picked.add(Number(el.dataset.id)); else ui.picked.delete(Number(el.dataset.id)); }),
  'match.approve': async (el) => {
    const picked = [...currentView().ui.picked];
    if (!picked.length) return;
    if (!(await confirmBox({ title: `Send ${picked.length} candidate${picked.length === 1 ? '' : 's'} to the company?`, text: 'The company sees their profile, skills and scores. Contact details stay hidden until the company shortlists them.', label: 'Send' }))) return;
    await api.post(`/admin/requirements/${el.dataset.id}/approve`, { body: { student_ids: picked } });
    currentView().ui.picked.clear();
    toast('Candidates sent', 'send');
    await reload();
  },
});

/* ---------- opportunities and interest ---------- */

const opportunities = {
  path: '/admin/opportunities',
  roles: ['admin'],
  title: 'Opportunities',
  ui: () => ({ tab: 'interests' }),
  async load(ctx) {
    const [o, i] = await Promise.all([api.get('/admin/opportunities', { signal: ctx.signal }), api.get('/admin/interests', { signal: ctx.signal, query: { status: 'sent' } })]);
    return { opportunities: o.opportunities, interests: i.interests };
  },
  view(d, ui) {
    const tabs = [['interests', `Student interest (${d.interests.length})`], ['list', `Opportunities (${d.opportunities.length})`]];
    return html`${pageHead('Opportunities', 'Roles you show to students. Students express interest; you recommend the right ones to the company.', html`<a class="btn" href="/admin/opportunities/new">${ic('plus')} New opportunity</a>`)}
      <div class="mb">${seg(tabs, ui.tab, 'opps.tab', 'Sections')}</div>
      ${ui.tab === 'interests'
        ? (d.interests.length ? table(['Student', 'Opportunity', 'Note', 'Can start', 'When', ''], d.interests.map((i) => [html`<b>${i.student}</b>`, i.opportunity, i.note || html`<span class="mu">None</span>`, i.start_when, ago(i.at),
          html`<div class="row nw"><button type="button" class="btn sm" data-act="interest.review" data-id="${i.id}" data-action="recommended">Recommend</button><button type="button" class="btn ghost sm" data-act="interest.review" data-id="${i.id}" data-action="declined">Decline</button></div>`]))
          : empty('check', 'Nothing waiting', 'New interest from students appears here.'))
        : (d.opportunities.length ? table(['Role', 'Company', 'Type', 'Skills', 'Pay', 'Status', ''], d.opportunities.map((o) => [html`<b>${o.title}</b>`, o.org_name, o.type, chips(o.skills), o.pay_text, statusTag(o.status),
          o.status === 'open' ? html`<button type="button" class="btn ghost sm" data-act="opp.close" data-id="${o.id}" data-title="${o.title}">Close</button>` : '']))
          : empty('brief', 'No opportunities', 'Add the first one.'))}`;
  },
};

const opportunityNew = {
  path: '/admin/opportunities/new',
  roles: ['admin'],
  nav: 'opportunities',
  title: 'New opportunity',
  view: () => html`${backLink('/admin/opportunities', 'Opportunities')}<div class="card" style="max-width:700px"><h2>New opportunity</h2>
    <form data-submit="opp.create" novalidate>${field('on-title', 'Role title', { name: 'title', minlen: 3, maxlength: 160 })}${field('on-org', 'Company name', { name: 'org_name', minlen: 2, maxlength: 160 })}
      <div class="fgrid">${selectField('on-type', 'Type', [['Job', 'Job'], ['Internship', 'Internship'], ['Project', 'Project']], { name: 'type', val: 'Job' })}${field('on-loc', 'Location', { name: 'location', req: false, maxlength: 60 })}${field('on-pay', 'Pay', { name: 'pay_text', req: false, maxlength: 60, ph: '₹3.5 to 5 LPA' })}</div>
      ${tagField('on-skills', 'Skills required', { name: 'skills', help: 'Students need these skills to be eligible.' })}<button class="btn" type="submit">Publish</button></form></div>`,
};

register({
  'opps.tab': (el) => update((ui) => { ui.tab = el.dataset.value; }),
  'interest.review': async (el) => {
    await api.post(`/admin/interests/${el.dataset.id}/review`, { body: { action: el.dataset.action } });
    toast(el.dataset.action === 'recommended' ? 'Recommended' : 'Declined', 'check');
    await reload();
  },
  'opp.close': async (el) => {
    if (!(await confirmBox({ title: `Close ${el.dataset.title}?`, text: 'Students can no longer express interest.', label: 'Close', danger: true }))) return;
    await api.post(`/admin/opportunities/${el.dataset.id}/close`);
    await reload();
  },
  'opp.create': (form) => submitForm(form, async (v) => {
    await api.post('/admin/opportunities', { body: { title: v.title, org_name: v.org_name, type: v.type, skills: v.skills, location: v.location, pay_text: v.pay_text } });
    toast('Opportunity published', 'check');
    await navigate('/admin/opportunities');
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- co-founder introductions ---------- */

const introductions = {
  path: '/admin/introductions',
  roles: ['admin'],
  title: 'Introductions',
  load: (ctx) => api.get('/admin/intros', { signal: ctx.signal, query: { status: ctx.query.get('status') || 'requested' } }),
  view(d, _ui, ctx) {
    const status = ctx.query.get('status') || 'requested';
    return html`${pageHead('Co-founder introductions', 'Startups cannot message co-founders directly. You decide whether to forward a request; the person then chooses.')}
      <div class="row mb">${filterSelect('Status', 'status', [['forwarded', 'Forwarded'], ['accepted', 'Accepted'], ['declined', 'Declined']], status === 'requested' ? '' : status, 'Waiting')}</div>
      ${d.intros.length
        ? table(['Startup', 'Co-founder', 'Their note', 'Requested', 'Status', ''], d.intros.map((i) => [html`<b>${i.startup}</b>`, i.cofounder, html`<span class="sm" style="white-space:pre-line">${i.note}</span>`, ago(i.at), statusTag(i.status),
          i.status === 'requested' ? html`<button type="button" class="btn sm" data-act="intro.forward" data-id="${i.id}">Forward</button>` : '']))
        : empty('link', 'No introductions here', 'Requests from startups appear here.')}`;
  },
};

register({
  'intro.forward': async (el) => {
    await api.post(`/admin/intros/${el.dataset.id}/forward`);
    toast('Forwarded to the co-founder', 'send');
    await reload();
  },
});

export const routes = [requirements, requirementDetail, opportunityNew, opportunities, introductions];
